import { useSeoMeta } from "@unhead/react";
import { useLocation, Link } from "react-router-dom";
import { useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Flame, Home, ArrowLeft } from "lucide-react";

const NotFound = () => {
  const location = useLocation();

  useSeoMeta({
    title: "404 - Page Not Found | Pollstr",
    description: "The page you are looking for could not be found. Return to the home page to continue browsing.",
  });

  useEffect(() => {
    console.error(
      "404 Error: User attempted to access non-existent route:",
      location.pathname
    );
  }, [location.pathname]);

  return (
    <div className="min-h-screen relative overflow-hidden bg-background dark flex items-center justify-center">
      {/* Animated Background */}
      <div className="fixed inset-0 -z-10 gradient-mesh dark:gradient-fire opacity-50" />
      <div className="fixed inset-0 -z-10 bg-gradient-to-b from-transparent via-background/50 to-background" />

      {/* Floating Orbs */}
      <div className="fixed top-20 right-20 w-72 h-72 bg-orange-500/20 rounded-full blur-3xl animate-float -z-10" />
      <div className="fixed bottom-20 left-20 w-96 h-96 bg-amber-500/15 rounded-full blur-3xl animate-float-slow -z-10" />

      <div className="glass dark:glass-dark rounded-3xl shadow-2xl border border-orange-500/20 dark:border-orange-500/30 animate-bounce-in overflow-hidden max-w-lg mx-4">
        <div className="relative py-16 px-8 text-center">
          {/* Background glow */}
          <div className="absolute inset-0 bg-gradient-to-br from-orange-500/5 via-transparent to-amber-500/5" />
          
          <div className="relative space-y-8">
            {/* Icon */}
            <div className="flex justify-center">
              <div className="relative">
                <div className="absolute inset-0 bg-orange-500/30 rounded-full blur-3xl animate-breathe" />
                <div className="relative glass dark:glass-dark p-10 rounded-full border-2 border-orange-500/30 shadow-2xl shadow-orange-500/20">
                  <Flame className="h-20 w-20 text-orange-500 animate-fire-dance" />
                </div>
              </div>
            </div>

            {/* Text */}
            <div className="space-y-3">
              <h1 className="text-8xl font-black bg-gradient-to-r from-orange-500 via-amber-500 to-orange-600 bg-clip-text text-transparent">
                404
              </h1>
              <h2 className="text-2xl font-bold text-foreground/80">
                Page Not Found
              </h2>
              <p className="text-foreground/50 font-medium max-w-sm mx-auto">
                The page you're looking for doesn't exist or has been moved.
              </p>
            </div>

            {/* Actions */}
            <div className="flex flex-col sm:flex-row gap-3 justify-center pt-4">
              <Button
                variant="outline"
                onClick={() => window.history.back()}
                className="glass dark:glass-dark border border-orange-500/20 hover:bg-orange-500/10 hover:border-orange-500/40 font-bold transition-all duration-300 group"
              >
                <ArrowLeft className="h-4 w-4 mr-2 group-hover:-translate-x-1 transition-transform" />
                Go Back
              </Button>
              <Link to="/">
                <Button className="bg-gradient-to-r from-orange-500 via-amber-500 to-orange-600 hover:shadow-xl hover:shadow-orange-500/30 transition-all duration-300 font-bold border-0 text-white w-full">
                  <Home className="h-4 w-4 mr-2" />
                  Return Home
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default NotFound;
